import JSFileDownloader from 'js-file-downloader'
import { difference, pick, sortBy } from 'lodash-es'
import type { Task } from 'redux-saga'
import { cancel, delay, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { call, take, fork, select } from 'typed-redux-saga'
import { reportPrice } from '#/configs/reports'
import { LIST_PAGE_SIZE } from '#/consts'
import { REPORT_VERSION } from '#/consts/reports'
import { logEvent } from '#/services/analytics'
// import { search } from '#/services/properties'
import { getProperties } from '#/services/v4/properties/client'
import * as services from '#/services/workspace'
import { GetMlsInfo } from '#/store/actions/properties'
import * as Actions from '#/store/actions/workspace'
import { initialState } from '#/store/reducers/workspace'
import { listItems } from '#/store/selectors/workspace'
import notification, { type NotificationShowOption } from '#/utils/notification'
import { reportName } from '#/utils/report'
import { actionBuilder, watchForCollection, watchForDocument } from '#/utils/saga'

function* watchForList(uid: string, listId: 'favorites' | 'hidden', workspaceId = 'default') {
  yield* watchForCollection(
    `workspaces/${uid}-${workspaceId}/lists/${listId}/items`,
    function* (snapshot) {
      const list: Record<
      string,
      TopHap.WorkspaceListItem | Pick<TopHap.WorkspaceListItem, 'status'>
      > = {}

      snapshot.docChanges().forEach(change => {
        if (change.type === 'removed') {
          list[change.doc.id] = { status: 'deleted' }
        } else {
          list[change.doc.id] = {
            ...change.doc.data(),
            status: 'created'
          }
        }
      })

      yield put(Actions.UpdateList({ listId, updates: list }))
    }
  )
}

function* watchForReports(uid: string, workspaceId = 'default') {
  yield* watchForCollection(
    `workspaces/${uid}-${workspaceId}/reports`,
    function* (snapshot) {
      const items = snapshot.docs.map(e => e.data()) as TopHap.WorkspaceReportItem[]
      yield put(Actions.UpdateReports(items))
    }
  )
}

function* watchForWorkspace({ user }: TopHap.AuthStateChangedAction) {
  try {
    yield put(Actions.UpdateStates(initialState))
    yield* fork(() => watchForList(user.uid, 'favorites'))
    yield* fork(() => watchForList(user.uid, 'hidden'))
    yield* fork(() => watchForReports(user.uid))
  } catch (err) {
    console.error('Watching For Workspace:', err)
  }
}

function _signInMessage(message: string) {
  notification.show({
    type: 'warning',
    title: '',
    message
  })
}

function* addToList(action: ReturnType<typeof Actions.AddToList>) {
  const user = yield* select((_: TopHap.StoreState) => _.user)
  const userId = user.uid
  const { propertyId, listId, workspaceId } = action.payload

  if (user.info.isAnonymous !== false) {
    _signInMessage(`Please Sign In to save ${listId === 'hidden' ? 'hidden' : 'favorite'} properties.`)
  }

  yield* actionBuilder(
    action,
    function* () {
      yield delay(300)
      yield* call(services.addToList, propertyId, listId, workspaceId)
    },
    { userId, propertyId, listId, workspaceId },
    { userId, propertyId, listId, workspaceId }
  )
}

function* removeFromList(action: ReturnType<typeof Actions.RemoveFromList>) {
  const userId = yield* select((_: TopHap.StoreState) => _.user.uid)
  const { propertyId, listId, workspaceId } = action.payload

  yield* actionBuilder(
    action,
    function* () {
      yield delay(300)
      yield* call(services.removeFromList, propertyId, listId, workspaceId)
    },
    { userId, propertyId, listId, workspaceId },
    { userId, propertyId, listId, workspaceId }
  )
}

function* updateLists() {
  const tasks: Record<string, Task> = {}

  while (true) {
    const action = yield* take<
    ReturnType<typeof Actions.AddToList> |
    ReturnType<typeof Actions.RemoveFromList>
    >([Actions.AddToList.type, Actions.RemoveFromList.type])
    const { propertyId, listId, workspaceId } = action.payload
    const taskId = `${propertyId}_${listId}_${workspaceId}`
    if (tasks[taskId]) {
      yield cancel(tasks[taskId])
    }

    tasks[taskId] = yield* fork(
      Actions.AddToList.match(action) ?
        addToList :
        removeFromList,
      action
    )
  }
}

function* listProperties(action: ReturnType<typeof Actions.ListProperties>) {
  const newMls: Record<string, TopHap.Property> = {}

  yield* actionBuilder(
    action,
    function* () {
      const { listId, reset } = action.payload
      const mls = yield* select((_: TopHap.StoreState) => _.properties.mls)
      const properties = yield* select((_: TopHap.StoreState) => _.workspace.properties[listId])

      let items = yield* select((_: TopHap.StoreState) => listItems(_, listId))
      if (!items.length) {
        yield* take(Actions.UpdateList)
        items = yield* select((_: TopHap.StoreState) => listItems(_, listId))
      }

      const ids = sortBy(items, e => -e.createdAt).map(e => e.propertyId)
      const newIds = difference(ids, properties.map(e => e.id))

      // const res = yield* call(search, {
      //   size: LIST_PAGE_SIZE,
      //   sort: [{
      //     option: 'status_timestamp',
      //     dir: 'asc'
      //   }],
      //   filters: {
      //     metricsFilter: {
      //       and: true,
      //       filters: [{
      //         and: true,
      //         subfilters: [
      //           {
      //             metric: 'property_id',
      //             operator: 'is',
      //             value: { value: newIds.slice(0, LIST_PAGE_SIZE).join(',') }
      //           }
      //         ]
      //       }]
      //     }
      //   },
      //   ultralight: true
      // } as TopHap.Service.SearchPropertiesRequest)

      const res = yield* call(getProperties, newIds.slice(0, LIST_PAGE_SIZE))

      res.items.forEach(e => {
        if (e.mls && !mls[e.mls]) {
          newMls[e.mls] = e
        }
      })

      let merged = reset ? res.items : [...properties, ...res.items]
      merged = ids
        .slice(0, merged.length)
        .map(id => merged.find(e => e.id === id) as TopHap.Property)

      return { listId, properties: merged }
    }
  )

  for (const key in newMls) {
    yield put(GetMlsInfo(newMls[key]))
  }
}

function* clearList(action: ReturnType<typeof Actions.ClearList>) {
  yield* actionBuilder(
    action,
    function* () {
      const { listId, workspaceId } = action.payload
      yield* call(services.clearList, listId, workspaceId)
    }
  )
}

function* requestReport(
  action: ReturnType<typeof Actions.RequestReport>
) {
  yield* actionBuilder(
    action,
    function* () {
      const user = yield* select((_: TopHap.StoreState) => _.user.info)
      const reportType = action.payload.payload.kind
      const report = reportName(reportType)

      const { workspaceId, payload } = action.payload
      const { clientPayload } = payload
      const res = yield* call(
        services.requestReport,
        {
          ...payload,
          version: payload.version || REPORT_VERSION[reportType],
          clientPayload: JSON.stringify({
            user: pick(user, ['displayName', 'photoUrl', 'email', 'phoneNumber', 'portal.company', 'portal.DRE']),
            ...clientPayload
          })
        },
        workspaceId
      )

      if (res.data.transactionId) {
        logEvent('complete_checkout', {
          transaction_id: res.data.transactionId,
          currency: 'USD',
          name: reportType.toLowerCase(),
          value: reportPrice[reportType],
          items: [
            {
              item_id: `report_${reportType.toLowerCase()}`,
              item_name: reportType.toLowerCase(),
              quantity: 1,
              price: reportPrice[reportType]
            }
          ]
        })
      }

      if (action.payload.callback) action.payload.callback()

      const task: Task = yield* fork(() =>
        watchForDocument(
          res.data.firestorePath,
          function* (snapshot) {
            const doc = snapshot.data() as TopHap.WorkspaceReportItem
            if (!doc) return

            if (doc.completed) {
              yield* call(notification.show, {
                type: 'success',
                title: `${report} complete!`,
                message: 'Your report has finished and is downloading.'
              } as NotificationShowOption)
              const url = doc.url
              const download = () => new JSFileDownloader({
                url,
                filename: doc.filename,
                contentType: false
              })

              yield* call(download)
              yield cancel(task)
            } else if (doc.error) {
              yield cancel(task)
              throw doc.errorMessage
            }
          }
        ))

      yield* call(notification.show, {
        type: 'info',
        title: `${report} requested!`,
        message: 'Your report is being created and will download when finished.'
      } as NotificationShowOption)

      return res
    },
    err => {
      if (action.payload.callback) action.payload.callback(err)
      return { err }
    }
  )
}

export default function* workspaceSaga() {
  yield takeLatest('AUTH_STATE_CHANGED', watchForWorkspace)
  yield* fork(updateLists)
  yield takeLatest(Actions.ListProperties, listProperties)
  yield takeLatest(Actions.ClearList, clearList)
  yield takeEvery(Actions.RequestReport, requestReport)
}
